import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { BottomNav } from "../components/BottomNav";
import {
  ReceiptDetailsPanel,
  TransactionDetailsPanel,
} from "../components/MatchComparePanels";
import { formatDate, formatMoney } from "../lib/format";
import { loadReceiptMedia } from "../lib/receiptMedia";
import { useAuth } from "../store/auth";
import type { ReceiptUploadResponse, Transaction } from "../types";

export function MatchReviewPage() {
  const navigate = useNavigate();
  const role = useAuth((s) => s.user?.role);
  const homePath = role === "admin" ? "/admin" : "/";
  const storedPayload = useMemo(() => {
    const raw =
      sessionStorage.getItem("lastUpload") || localStorage.getItem("lastUpload");
    if (!raw) return null;
    try {
      return JSON.parse(raw) as ReceiptUploadResponse & { txnHint?: string };
    } catch {
      return null;
    }
  }, []);
  const [payload, setPayload] = useState(storedPayload);

  const hintId = payload?.txnHint ? Number(payload.txnHint) || null : null;
  const [selected, setSelected] = useState<number | null>(
    hintId ?? payload?.suggestions[0]?.transaction_id ?? null,
  );
  const [selectedTxn, setSelectedTxn] = useState<Transaction | null>(null);
  const [hintTxn, setHintTxn] = useState<Transaction | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [savedForLater, setSavedForLater] = useState(false);
  const alreadyMatchedId = payload?.already_matched_transaction_id ?? null;
  const alreadyMatchedReceiptId = payload?.already_matched_receipt_id ?? null;
  const alreadyUploaded = Boolean(payload?.already_uploaded);

  useEffect(() => {
    if (!payload?.receipt?.id) return;
    let cancelled = false;
    let revoke: (() => void) | undefined;
    const r = payload.receipt;
    const pdf =
      (r.content_type || "").toLowerCase().includes("pdf") ||
      r.original_filename.toLowerCase().endsWith(".pdf");
    setIsPdf(pdf);

    (async () => {
      try {
        const media = await loadReceiptMedia(r.id, pdf);
        if (cancelled) {
          media.revoke();
          return;
        }
        revoke = media.revoke;
        setPreviewUrl(media.previewUrl);
        setFileUrl(media.fileUrl);
      } catch (e) {
        if (!cancelled) {
          console.error(e);
        }
      }
    })();

    return () => {
      cancelled = true;
      revoke?.();
    };
  }, [payload?.receipt?.id]);

  useEffect(() => {
    if (!selected) {
      setSelectedTxn(null);
      return;
    }
    let cancelled = false;
    const fromSuggestion =
      payload?.suggestions.find((s) => s.transaction_id === selected)?.transaction ??
      null;
    if (fromSuggestion) setSelectedTxn(fromSuggestion);

    (async () => {
      try {
        const full = await api.request<Transaction>(`/transactions/${selected}`);
        if (!cancelled) setSelectedTxn(full);
      } catch (e) {
        if (!cancelled && !fromSuggestion) {
          setError(e instanceof Error ? e.message : "Could not load transaction");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, payload?.suggestions]);

  useEffect(() => {
    if (!hintId) return;
    let cancelled = false;
    api
      .request<Transaction>(`/transactions/${hintId}`)
      .then((t) => {
        if (!cancelled) setHintTxn(t);
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [hintId]);

  // OCR runs in the background after upload, so keep the stored payload in sync
  const persist = useCallback(
    (next: ReceiptUploadResponse, txnHint?: string) => {
      const merged = { ...next, txnHint };
      setPayload(merged);
      const raw = JSON.stringify(merged);
      try {
        localStorage.setItem("lastUpload", raw);
      } catch {
        /* ignore quota */
      }
      sessionStorage.setItem("lastUpload", raw);
    },
    [],
  );

  const receiptId = payload?.receipt?.id;
  const extractionStatus = payload?.receipt?.extraction_status ?? "done";
  const txnHint = payload?.txnHint;

  useEffect(() => {
    if (!receiptId || extractionStatus !== "pending") return;
    let cancelled = false;
    let timer = 0;

    const poll = async () => {
      try {
        const next = await api.request<ReceiptUploadResponse>(
          `/receipts/${receiptId}/upload-result`,
        );
        if (cancelled) return;
        if (next.receipt.extraction_status === "pending") {
          timer = window.setTimeout(poll, 1500);
          return;
        }
        persist(next, txnHint);
      } catch {
        if (!cancelled) timer = window.setTimeout(poll, 3000);
      }
    };

    timer = window.setTimeout(poll, 1200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [receiptId, extractionStatus, txnHint, persist]);

  // Suggestions only arrive once OCR lands
  useEffect(() => {
    const best = payload?.suggestions?.[0]?.transaction_id;
    if (best != null) setSelected((cur) => cur ?? best);
  }, [payload?.suggestions]);

  if (!payload) {
    return (
      <div className="app-shell">
        <div className="panel">
          <p>No recent upload found.</p>
          <Link className="btn btn-primary" to="/capture">
            Capture a receipt
          </Link>
        </div>
        <BottomNav />
      </div>
    );
  }

  const { receipt, extraction, suggestions } = payload;
  const waiting =
    !hintId && (payload.waiting_for_statement || suggestions.length === 0);
  const hintNotSuggested =
    hintTxn != null && !suggestions.some((s) => s.transaction_id === hintTxn.id);
  const replacingFlagged = selectedTxn?.status === "insufficient";
  const twinCandidates = suggestions.filter((s) => {
    if (!s.transaction || extraction.amount == null) return false;
    const ra = Number(extraction.amount);
    const ta = Number(s.transaction.amount);
    if (Number.isNaN(ra) || Number.isNaN(ta)) return false;
    return Math.abs(ra - ta) <= Math.max(0.05, Math.abs(ta) * 0.02);
  });
  const showOneReceiptWarning = twinCandidates.length >= 2;
  const selectedSuggestion = suggestions.find(
    (s) => s.transaction_id === selected,
  );

  async function confirm() {
    if (!selected) {
      setError("Select a transaction to match");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.request(`/transactions/${selected}/confirm-match`, {
        method: "POST",
        body: JSON.stringify({ receipt_id: receipt.id }),
      });
      sessionStorage.removeItem("lastUpload");
      localStorage.removeItem("lastUpload");
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Match failed");
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    setBusy(true);
    try {
      if (
        selected &&
        !replacingFlagged &&
        suggestions.some((s) => s.transaction_id === selected)
      ) {
        await api.request(`/transactions/${selected}/reject-match`, {
          method: "POST",
          body: JSON.stringify({ notes: "User rejected suggestion" }),
        });
      }
      navigate(`/capture`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Reject failed");
    } finally {
      setBusy(false);
    }
  }

  function saveForLater() {
    sessionStorage.removeItem("lastUpload");
    localStorage.removeItem("lastUpload");
    setSavedForLater(true);
  }

  async function overruleMatch() {
    const receiptId = payload?.receipt?.id;
    if (!receiptId || !alreadyMatchedId) return;
    const kept =
      alreadyMatchedReceiptId && alreadyMatchedReceiptId !== receiptId
        ? `scan #${alreadyMatchedReceiptId} on transaction #${alreadyMatchedId}`
        : `transaction #${alreadyMatchedId}`;
    if (
      !window.confirm(
        `This looks like the receipt already on ${kept}. Overrule that and match this copy to another card line? The existing match stays.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const next = await api.request<ReceiptUploadResponse>(
        `/receipts/${receiptId}/overrule`,
        { method: "POST" },
      );
      persist(next, txnHint);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not overrule");
    } finally {
      setBusy(false);
    }
  }

  async function retryExtraction() {
    setBusy(true);
    setError(null);
    try {
      const next = await api.request<ReceiptUploadResponse>(
        `/receipts/${receipt.id}/reextract`,
        { method: "POST" },
      );
      persist(next, txnHint);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not retry");
    } finally {
      setBusy(false);
    }
  }

  if (extractionStatus === "pending") {
    return (
      <div className="app-shell">
        <div
          className="panel stack"
          style={{ textAlign: "center", padding: "2rem 1.25rem" }}
        >
          <div style={{ fontSize: "2.5rem" }}>⏳</div>
          <h2>Reading your receipt…</h2>
          <p className="muted">
            The photo is saved. We are extracting the merchant, amount and date, which
            takes a few seconds. You can leave this screen — the receipt shows up under
            Pending with its suggested match.
          </p>
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Uploaded receipt"
              style={{
                maxHeight: "38vh",
                objectFit: "contain",
                borderRadius: "0.5rem",
              }}
            />
          )}
          <button
            className="btn btn-ghost"
            type="button"
            onClick={() => navigate(homePath)}
          >
            Back to open items
          </button>
        </div>
        <BottomNav />
      </div>
    );
  }

  if (extractionStatus === "failed") {
    return (
      <div className="app-shell">
        <div
          className="panel stack"
          style={{ textAlign: "center", padding: "2rem 1.25rem" }}
        >
          <div style={{ fontSize: "2.5rem" }}>⚠️</div>
          <h2>Could not read this receipt</h2>
          <p className="muted">
            The photo is saved, but the scan could not be read
            {payload.receipt.extraction_error
              ? ` (${payload.receipt.extraction_error})`
              : ""}
            . Try again, or capture a clearer photo.
          </p>
          {error && <div className="alert">{error}</div>}
          <button
            className="btn btn-primary"
            type="button"
            disabled={busy}
            onClick={retryExtraction}
          >
            {busy ? "Retrying…" : "Try reading it again"}
          </button>
          <button
            className="btn btn-ghost"
            type="button"
            onClick={() => navigate("/capture")}
          >
            Capture a new photo
          </button>
        </div>
        <BottomNav />
      </div>
    );
  }

  if (alreadyMatchedId) {
    return (
      <div className="app-shell">
        <div
          className="panel stack"
          style={{ textAlign: "center", padding: "2rem 1.25rem" }}
        >
          <div style={{ fontSize: "2.5rem" }}>✓</div>
          <h2>Already matched</h2>
          <p className="muted">
            {alreadyMatchedReceiptId &&
            alreadyMatchedReceiptId !== payload.receipt.id
              ? `This looks like scan #${alreadyMatchedReceiptId}, already linked to transaction #${alreadyMatchedId}. Overrule to match this copy to another card line. That match stays.`
              : `This photo is already linked to transaction #${alreadyMatchedId}. Overrule to use it on another card line as well. That match stays.`}
          </p>
          {error && <div className="alert">{error}</div>}
          <button
            className="btn btn-primary"
            type="button"
            disabled={busy}
            onClick={overruleMatch}
          >
            {busy ? "Opening…" : "Overrule — match this copy"}
          </button>
          <Link className="btn btn-ghost" to={`/transactions/${alreadyMatchedId}`}>
            Open transaction #{alreadyMatchedId}
          </Link>
          {alreadyMatchedReceiptId && alreadyMatchedReceiptId !== payload.receipt.id && (
            <Link className="btn btn-ghost" to={`/receipts/${alreadyMatchedReceiptId}`}>
              Open matched scan #{alreadyMatchedReceiptId}
            </Link>
          )}
          <Link className="btn btn-ghost" to={homePath}>
            Back to open items
          </Link>
        </div>
        <BottomNav />
      </div>
    );
  }

  if (done) {
    return (
      <div className="app-shell">
        <div
          className="panel stack"
          style={{ textAlign: "center", padding: "2rem 1.25rem" }}
        >
          <div style={{ fontSize: "2.5rem" }}>✓</div>
          <h2>Matched</h2>
          <p className="muted">Receipt permanently linked to the transaction.</p>
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => navigate(homePath)}
          >
            Back to open items
          </button>
        </div>
        <BottomNav />
      </div>
    );
  }

  if (savedForLater) {
    return (
      <div className="app-shell">
        <div
          className="panel stack"
          style={{ textAlign: "center", padding: "2rem 1.25rem" }}
        >
          <div style={{ fontSize: "2.5rem" }}>⌛</div>
          <h2>Saved for later</h2>
          <p className="muted">
            Your receipt stays in Pending until the credit card statement is imported.
            We will suggest a match automatically when it appears.
          </p>
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => navigate(homePath)}
          >
            Back to home
          </button>
          <button
            className="btn btn-ghost"
            type="button"
            onClick={() => navigate("/capture")}
          >
            Scan another receipt
          </button>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="app-shell match-review">
      <header className="topbar">
        <div>
          <h2 style={{ fontSize: "1.35rem" }}>
            {waiting ? "Receipt saved" : "Confirm match"}
          </h2>
          <p className="muted" style={{ margin: 0 }}>
            {waiting
              ? "No card transaction yet — you can scan before the CSV arrives"
              : `Compare both sides, then confirm · ${receipt.ai_provider}`}
          </p>
        </div>
      </header>

      {receipt.dedupe_overruled && (
        <div className="alert alert-ok" style={{ marginBottom: "0.85rem" }}>
          Duplicate check overruled. Match this copy to its own card line. The earlier
          match stays in place.
        </div>
      )}

      {alreadyUploaded && (
        <div className="alert alert-ok" style={{ marginBottom: "0.85rem" }}>
          This photo was already uploaded — continuing with the existing scan instead of
          creating another copy.
        </div>
      )}

      {receipt.ai_provider === "mock" && (
        <div className="alert" style={{ marginBottom: "0.85rem" }}>
          OCR is in demo mode — extracted data is fake. Do not confirm unless the
          amounts clearly match. Ask an admin to set a real AI key
          (Grok/OpenAI/Claude).
        </div>
      )}

      {!waiting && (
        <div className="panel stack" style={{ marginBottom: "0.85rem" }}>
          <div className="row between" style={{ alignItems: "baseline" }}>
            <strong style={{ fontSize: "0.95rem" }}>Suggested transactions</strong>
            <span className="muted" style={{ fontSize: "0.8rem" }}>
              Tap one to compare
            </span>
          </div>
          {showOneReceiptWarning && (
            <div className="alert" style={{ margin: 0 }}>
              Several card lines look similar (same day / amount). One scan can only
              confirm <strong>one</strong> transaction — pick the correct line, then
              capture a separate receipt for the other.
            </div>
          )}
          <div className="match-suggestion-list">
            {hintNotSuggested && hintTxn && (
              <button
                type="button"
                className={`match-suggestion${selected === hintTxn.id ? " active" : ""}`}
                onClick={() => setSelected(hintTxn.id)}
              >
                <div className="row between">
                  <span className="merchant">{hintTxn.merchant_name}</span>
                  <span className="badge badge-suggested">Linked</span>
                </div>
                <div className="row between" style={{ marginTop: "0.25rem" }}>
                  <span className="muted" style={{ fontSize: "0.8rem" }}>
                    {formatDate(hintTxn.transaction_date)}
                  </span>
                  <span className="amount" style={{ fontSize: "1rem" }}>
                    {formatMoney(hintTxn.amount, hintTxn.currency)}
                  </span>
                </div>
              </button>
            )}
            {suggestions.map((s) => {
              const t = s.transaction;
              if (!t) return null;
              const active = selected === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`match-suggestion${active ? " active" : ""}`}
                  onClick={() => setSelected(t.id)}
                >
                  <div className="row between">
                    <span className="merchant">{t.merchant_name}</span>
                    <span className="badge badge-suggested">
                      {Math.round(s.confidence * 100)}%
                    </span>
                  </div>
                  <div className="row between" style={{ marginTop: "0.25rem" }}>
                    <span className="muted" style={{ fontSize: "0.8rem" }}>
                      {formatDate(t.transaction_date)}
                    </span>
                    <span className="amount" style={{ fontSize: "1rem" }}>
                      {formatMoney(t.amount, t.currency)}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="match-compare">
        {waiting ? (
          <div className="panel stack match-side">
            <strong>Waiting for credit card CSV</strong>
            <p className="muted" style={{ margin: 0 }}>
              Keep this receipt. When admin imports the statement, we will try to
              match it automatically. You will see it under Pending on the home
              screen.
            </p>
          </div>
        ) : selectedTxn ? (
          <TransactionDetailsPanel
            txn={selectedTxn}
            confidence={selectedSuggestion?.confidence}
            reasons={selectedSuggestion?.reasons}
          />
        ) : (
          <div className="panel match-side">
            <p className="muted" style={{ margin: 0 }}>
              Select a suggested transaction to see full details.
            </p>
          </div>
        )}

        <ReceiptDetailsPanel
          receipt={receipt}
          extraction={extraction}
          previewUrl={previewUrl}
          fileUrl={fileUrl}
          isPdf={isPdf}
        />
      </div>

      {error && (
        <div className="alert" style={{ marginTop: "0.85rem" }}>
          {error}
        </div>
      )}

      <div className="stack" style={{ marginTop: "1rem" }}>
        {!waiting && replacingFlagged && (
          <div className="alert alert-warn" style={{ margin: 0 }}>
            This transaction's current receipt was flagged as insufficient. Confirming
            replaces it with this scan; the old scan goes back to your pending receipts.
          </div>
        )}
        {!waiting && (
          <>
            <button
              type="button"
              className="btn btn-primary btn-block"
              disabled={busy || !selected}
              onClick={confirm}
            >
              {busy
                ? "Saving…"
                : replacingFlagged
                  ? "Replace receipt — save permanently"
                  : "Confirm match — save permanently"}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-block"
              disabled={busy}
              onClick={reject}
            >
              Wrong match — pick again
            </button>
          </>
        )}
        <button
          type="button"
          className="btn btn-primary btn-block"
          disabled={busy}
          onClick={saveForLater}
          style={waiting ? undefined : { order: 3 }}
        >
          {waiting
            ? "Done — keep waiting for statement"
            : "Save for later (wait for statement)"}
        </button>
        {!waiting && (
          <button
            type="button"
            className="btn btn-ghost btn-block"
            disabled={busy}
            onClick={() => {
              sessionStorage.removeItem("lastUpload");
              localStorage.removeItem("lastUpload");
              navigate(homePath);
            }}
          >
            Cancel
          </button>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
